import React, { useState, useEffect,useRef } from "react";
import { workModes, managersData } from "./teams";
import translations from "./translations";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import Step5 from "./components/Step5";

const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);
const allTags = shuffleArray(
  managersData.flatMap(manager =>
    manager.tag.map(tag => ({ ...tag, managerId: manager.id }))
  )
);

const TeamConfigurator = () => {
  const supportedLangs = ["it", "en"];
  const urlLang = window.location.pathname.split("/")[1];
  const lang = supportedLangs.includes(urlLang) ? urlLang : "it";
  const t = translations[lang];
  const [hoveredManager, setHoveredManager] = useState(null);
  const [currentManagerIndex, setCurrentManagerIndex] = useState(0);
  const [selectedManagers, setSelectedManagers] = useState(
    JSON.parse(localStorage.getItem("selectedManagers")) || []
  );

  const [currentStep, setCurrentStep] = useState(
    JSON.parse(localStorage.getItem("currentStep")) || 1
  );

  const [answers, setAnswers] = useState(
    JSON.parse(localStorage.getItem("answers")) || {}
  );

  const [selectedWorkMode, setSelectedWorkMode] = useState(
    JSON.parse(localStorage.getItem("selectedWorkMode")) || []
  );

  const startDateTime = localStorage.getItem("startDateTime") || "Non disponibile";

  const previousStep = useRef(null);

  useEffect(() => {
    if (previousStep.current !== null && previousStep.current !== currentStep) {
      const element = document.getElementById("crea-team");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    previousStep.current = currentStep;
  }, [currentStep]);
  


  // Salvo i dati nel Local Storage quando cambiano
  useEffect(() => {
    localStorage.setItem("selectedManagers", JSON.stringify(selectedManagers));
    localStorage.setItem("currentStep", JSON.stringify(currentStep));
    localStorage.setItem("answers", JSON.stringify(answers));
    localStorage.setItem("selectedWorkMode", JSON.stringify(selectedWorkMode));
  }, [selectedManagers, currentStep, answers, selectedWorkMode]);
  


  useEffect(() => {
    if (currentStep === 2 && !localStorage.getItem("startDateTime")) {
      const now = new Date().toISOString();
      localStorage.setItem("startDateTime", now);
    }
  }, [currentStep]);


  // Funzione per trasformare i dati in un formato adatto al grafico
  const prepareChartData = (selectedManagers, answers, lang) => {
    let allQuestions = new Set();
  
    managersData.forEach(manager => {
      manager.questions.forEach(q => {
        allQuestions.add(JSON.stringify({ text: q[lang], key: q.id })); // salvi entrambi
      });
    });
  
    const data = Array.from(allQuestions).map(q => {
      const { text, key } = JSON.parse(q);
      let obj = { question: text };
  
      managersData.forEach(manager => {
        const isSelected = selectedManagers.some(sel => sel.id === manager.id);
  
        obj[manager.id] =
          isSelected && manager.questions.some(q => q.id === key)
            ? parseFloat(answers[manager.id]?.[key] || 5)
            : 0;
      });
  
      return obj;
    });
  
    return data;
  };
  
  


  
  
  

  // Funzione per aggiornare le risposte
  const handleRangeChange = (managerId, question, value, event) => {
    const percentuale = (value / 10) * 100;
  
    // Imposta direttamente sulla barra specifica
    event.target.style.setProperty("--progress", `${percentuale}%`);
  
    setAnswers(prev => {
      const updatedAnswers = {
        ...prev,
        [managerId]: {
          ...prev[managerId],
          [question]: value
        }
      };
      localStorage.setItem("answers", JSON.stringify(updatedAnswers));
      return updatedAnswers;
    });
  };
  
  // Funzione per selezionare/deselezionare manager
  const toggleManagerSelection = (manager) => {
    setSelectedManagers((prev) => {
      if (prev.some((m) => m.id === manager.id)) {
        return prev.filter((m) => m.id !== manager.id);
      } else {
        return [...prev, manager];
      }
    });
  };

  // Resetta il Local Storage e lo stato
  const resetSelection = () => {
    localStorage.removeItem("selectedManagers");
    localStorage.removeItem("currentStep");
    localStorage.removeItem("answers");
    localStorage.removeItem("startDateTime");
    setSelectedManagers([]);
    setAnswers({});
    setCurrentManagerIndex(0);
    setSelectedWorkMode([])
    setCurrentStep(1);
  };

  const backToStart = () => {
    setCurrentStep(1);
  }

  const resetLocalStorage = () => {
    localStorage.removeItem("selectedManagers");
    localStorage.removeItem("currentStep");
    localStorage.removeItem("answers");
    localStorage.removeItem("startDateTime");
    setSelectedManagers([]);
    setAnswers({});
    setCurrentManagerIndex(0);
    setSelectedWorkMode([])
  };

  useEffect(() => {
    if (currentStep === 8) { // Appena prima della pagina "Grazie!"
      // Verifica che i dati necessari siano presenti
      if (!selectedManagers.length || !Object.keys(answers).length || !selectedWorkMode.length) {
        // Se i dati sono vuoti, resetta lo stato e porta l'utente allo Step 1
        resetLocalStorage();
        setCurrentStep(1);  // Porta all'inizio del flusso
        return;  // Interrompe l'uso del fetch
      }
  
      // Otteniamo tutte le domande possibili per ogni manager
      const allQuestions = [...new Set(managersData.flatMap(manager => manager.questions))];
  
      // Struttura le risposte, includendo solo le domande pertinenti ai manager selezionati
      const formattedAnswers = selectedManagers.reduce((acc, manager) => {
        acc[manager.id] = manager.questions.map(question => {
          const rawValue = answers[manager.id]?.[question.id];
      
          // se non c’è risposta usa 5 (valore di default dello slider)
          const value0to10 = rawValue !== undefined ? Number(rawValue) : 5;
      
          return {
            question: question[lang],           // testo nella lingua giusta
            percentage: `${value0to10 * 10}%`   // ora è 0-100
          };
        });
        return acc;
      }, {});
  
      // Dati finali da inviare
      const finalResults = {
        selectedManagers: selectedManagers.map(manager => ({
          id: manager.id,
          name: manager.name
        })),
        answers: formattedAnswers,
        selectedWorkMode,
        userData: {
          name: answers.name || "Non specificato",
          email: answers.mail || "Non specificato",
          telefono: answers.tel || "Non specificato",
          attivita: answers.attivita || "Non specificato",
          sitoWeb: answers.sito || "Non specificato",
          dataInizio: startDateTime
        }
      };
  
      // Invia i dati via email al file PHP
      fetch("https://mirai-bay.com/configuratore/sendmail.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(finalResults)
      })
        .then(response => response.json())
        .then(data => console.log("📥 Risposta dal server:", data))
        .then(resetLocalStorage)
        .catch(error => console.error("❌ Errore nell'invio dei dati:", error));
    }
  }, [currentStep]);
  
  
  
  return (
    <div className="team-configurator">
      
      {/* Step 1 - Selezione Manager */}
      {currentStep === 1 && (
        <Step1
          setCurrentStep={setCurrentStep}
          selectedManagers={selectedManagers}
          managersData={managersData}
          hoveredManager={hoveredManager}
          setHoveredManager={setHoveredManager}
          toggleManagerSelection={toggleManagerSelection}
          setSelectedManagers={setSelectedManagers}
          t={t}
          lang={lang}
        />
      )}

      {/* Step 2 - Domande per ogni manager selezionato */}
      {currentStep === 2 && selectedManagers.length > 0 && (
         <Step2
         currentManagerIndex = {currentManagerIndex}
         handleRangeChange = {handleRangeChange}
         answers = {answers}
         setCurrentManagerIndex = {setCurrentManagerIndex}
         setCurrentStep= {setCurrentStep}
         selectedManagers= {selectedManagers}
         selectedWorkMode ={selectedWorkMode}
         t={t}
         lang={lang}
       />       
      )}
  
      {/* Step 3 - Selezione modalità di lavoro */}
      {currentStep === 3 && (
         <Step3
         setCurrentManagerIndex = {setCurrentManagerIndex}
         setCurrentStep= {setCurrentStep}
         selectedWorkMode = {selectedWorkMode}
         setSelectedWorkMode = {setSelectedWorkMode}
         t={t}
         lang={lang}
       /> 
      )}
  
      {/* Step 4 - La tua Priority Matrix */}   
      {currentStep === 4 && (
      <Step4
      selectedManagers= {selectedManagers}
      setCurrentStep= {setCurrentStep}
      prepareChartData = {prepareChartData}
      answers = {answers}
      workModes = {workModes}
      selectedWorkMode = {selectedWorkMode}
      setCurrentManagerIndex = {setCurrentManagerIndex}
      resetSelection = {resetSelection}
      t={t}
      lang={lang}
    /> 
    )}

      {/* Step 5 - Inserimento dati utente */}
      <Step5
          setCurrentStep={setCurrentStep}
          answers={answers}
          currentStep={currentStep}
          resetSelection={backToStart}
          setAnswers={setAnswers}
          t={t}
          lang={lang}
        />

      {/* Sezione TAG SEMPRE VISIBILE */}
      {currentStep != 8 && ( <div className="tags-section">
        <div className="tags-wrapper">
          {[0, 1, 2].map((rowIndex) => (
            <div key={rowIndex} className={`tags-row row-${rowIndex + 1}`}>
              <div className="tags-content">
                {allTags
                  .filter((_, index) => index % 3 === rowIndex)
                  .map((tag, index) => {
                    const manager = selectedManagers.find((m) => m.id === tag.managerId);
                    const isHighlighted = !!manager;
                    const tagColor = manager ? manager.color : "transparent";
  
                    return (
                      <div
                        key={index}
                        className={`tag-card ${isHighlighted ? "highlighted" : ""}`}
                        style={isHighlighted ? { "--tag-color": tagColor } : {}}
                      >
                        <img src={tag.image} alt={tag.role} className="tag-image" />
                        <p className="tag-text">{tag.role}</p>
                      </div>
                    );
                  })}
                {/* Duplicato per un effetto infinito */}
                {allTags
                  .filter((_, index) => index % 3 === rowIndex)
                  .map((tag, index) => {
                    const manager = selectedManagers.find((m) => m.id === tag.managerId);
                    const isHighlighted = !!manager;
                    const tagColor = manager ? manager.color : "transparent";
  
                    return (
                      <div
                        key={`dup-${index}`}
                        className={`tag-card ${isHighlighted ? "highlighted" : ""}`}
                        style={isHighlighted ? { "--tag-color": tagColor } : {}}
                      >
                        <img src={tag.image} alt={tag.role} className="tag-image" />
                        <p className="tag-text">{tag.role}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
};

export default TeamConfigurator;